import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, Video, PhoneOff, Mic } from "lucide-react";
import { PhoneFrame } from "./PhoneFrame";

const SCRIPTS = {
  recharge: {
    q: "Recharge 50 kWh",
    a: "Done. Rp 75.000 paid via QRIS — token sent straight to your meter. New balance: 58.4 kWh.",
  },
  usage: {
    q: "Why is my usage high?",
    a: "Your AC ran ~9 h/day this week, +18% vs last week. Want a 9 PM reminder to switch it off?",
  },
  meter: {
    q: "My meter shows PERIKSA",
    a: "That means the meter needs a check. Let's hop on a quick video call — I'll guide you step by step.",
    call: true,
  },
};

const Bubble = ({ m }) => (
  <motion.div
    initial={{ opacity: 0, y: 10, scale: 0.97 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    className={`max-w-[85%] rounded-2xl px-3 py-2 text-[12.5px] leading-snug ${
      m.from === "me" ? "self-end rounded-br-md bg-[var(--dash)] text-black" : "self-start rounded-bl-md bg-white/[0.07] text-zinc-100"
    }`}
  >
    {m.text}
  </motion.div>
);

const CallOverlay = ({ onEnd }) => {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const captions = ["Point your camera at the meter.", "I see the PERIKSA code.", "Press 1-2-3 then Enter.", "Great — meter is back online."];
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-30 flex flex-col items-center justify-between bg-[#07080b] px-5 pb-8 pt-12" data-testid="ai-video-call-overlay">
      <p className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-zinc-400">Dash AI · {`0:${String(sec).padStart(2, "0")}`}</p>
      <div className="relative h-40 w-40">
        <div className="pulse-ring absolute inset-0 rounded-full border border-[var(--dash)]" />
        <img src="/assets/orb.jpg" alt="Dash AI on call" className="h-full w-full rounded-full object-cover [mask-image:radial-gradient(circle,black_55%,transparent_72%)]" />
      </div>
      <p className="min-h-[40px] text-center text-sm text-zinc-100" data-testid="ai-call-caption">{captions[Math.min(captions.length - 1, Math.floor(sec / 2))]}</p>
      <div className="flex gap-4">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-white/10 text-zinc-100"><Mic size={18} /></span>
        <button onClick={onEnd} data-testid="ai-call-end-button" aria-label="End call" className="grid h-12 w-12 place-items-center rounded-full bg-red-500 text-white transition-transform hover:scale-105">
          <PhoneOff size={18} />
        </button>
      </div>
    </motion.div>
  );
};

export const PhoneAI = () => {
  const [msgs, setMsgs] = useState([{ from: "ai", text: "Good evening, Rina. Unit 12B has 8.4 kWh left — about 2 days at your usage." }]);
  const [typing, setTyping] = useState(false);
  const [call, setCall] = useState(false);
  const [canCall, setCanCall] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [msgs, typing]);

  const ask = (key) => {
    if (typing) return;
    const s = SCRIPTS[key];
    setMsgs((m) => [...m, { from: "me", text: s.q }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMsgs((m) => [...m, { from: "ai", text: s.a }]);
      if (s.call) setCanCall(true);
    }, 900);
  };

  return (
    <PhoneFrame testId="mockup-ai-assistant">
      <div className="flex items-center gap-2 border-b border-white/5 px-4 pb-3 pt-9">
        <img src="/assets/orb.jpg" alt="" className="h-8 w-8 rounded-full object-cover" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-zinc-50">Dash AI</p>
          <p className="flex items-center gap-1 text-[11px] text-emerald-400"><Lock size={10} /> End-to-end encrypted</p>
        </div>
        <button onClick={() => setCall(true)} data-testid="ai-video-call-button" aria-label="Video call Dash AI" className="grid h-8 w-8 place-items-center rounded-full bg-[var(--dash)] text-black transition-transform hover:scale-110">
          <Video size={15} />
        </button>
      </div>
      <div ref={listRef} className="no-scrollbar flex flex-1 flex-col gap-2 overflow-y-auto px-3 py-3" data-testid="ai-chat-messages">
        {msgs.map((m, i) => <Bubble key={i} m={m} />)}
        {typing && (
          <div className="flex gap-1 self-start rounded-2xl bg-white/[0.07] px-3 py-3" data-testid="ai-typing-indicator">
            {[0, 1, 2].map((d) => (
              <motion.span key={d} className="h-1.5 w-1.5 rounded-full bg-zinc-300" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ repeat: Infinity, duration: 1, delay: d * 0.15 }} />
            ))}
          </div>
        )}
        {canCall && !typing && (
          <button onClick={() => setCall(true)} data-testid="ai-start-call-inline" className="self-start rounded-full border border-[var(--dash)] px-3 py-1.5 text-xs text-[var(--dash)] transition-colors hover:bg-[var(--dash)] hover:text-black">
            Start video call
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 border-t border-white/5 p-3">
        {Object.entries(SCRIPTS).map(([k, s]) => (
          <button key={k} onClick={() => ask(k)} data-testid={`ai-prompt-${k}`} className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-[11.5px] text-zinc-200 transition-colors hover:border-[var(--dash)] hover:text-[var(--dash)]">
            {s.q}
          </button>
        ))}
      </div>
      <AnimatePresence>{call && <CallOverlay onEnd={() => setCall(false)} />}</AnimatePresence>
    </PhoneFrame>
  );
};
